import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsDesktop } from '@xing-com/crate-hooks-use-is-desktop';
import { useNetworkTracking } from '@xing-com/crate-network-tracking';
import { useLoginAppUrl } from '@xing-com/crate-xinglet';
import { GridContainer } from '@xing-com/grid';
import { LightMode } from '@xing-com/theme-mode-switcher';

import backgroundWide from './assets/network-background-wide.svg';
import networkImageWideDE from './assets/network-image-wide-de.svg';
import networkImageWideEN from './assets/network-image-wide-en.svg';
import { Styled } from './discover-network.styled';

export const DiscoverNetwork: FC = () => {
  const { trackNetworkNWTExtended } = useNetworkTracking();
  const isDesktop = useIsDesktop();
  const loginUrl = useLoginAppUrl();
  const { locale } = useIntl();
  const isLocaleDE = locale === 'de';

  const ctaLinkTracking =
    '?sc_o=contacts_network_personal_network_register_click&signup_channel=minireg_network_personal_network';
  const destParam =
    '&dest_url=' +
    encodeURIComponent('/network#profile-recommendations?origin=network');
  const buttonUrl = loginUrl + ctaLinkTracking + destParam;

  const onClickCta = (): void => {
    trackNetworkNWTExtended({
      event: 'opened',
      element: 'register',
      elementDetail: 'personal_network',
      moduleTitle: 'personal_network',
    });
  };

  return isDesktop ? (
    <Styled.CustomSection data-qa="discover-network-section">
      <Styled.GridContainerMargin
        variant="expanded"
        data-qa="discover-network-container"
      >
        <Styled.GridItemRow data-qa="discover-network-row">
          <Styled.InteractionContainer>
            <Styled.HeadlineWide noMargin size="small">
              <FormattedMessage id="NETWORK_LOGGED_OUT_DISCOVER_HEADLINE" />
            </Styled.HeadlineWide>
            <Styled.DiscoverButton
              variant="secondary"
              size="large"
              to={buttonUrl}
              onClick={onClickCta}
            >
              <FormattedMessage id="NETWORK_LOGGED_OUT_DISCOVER_CTA" />
            </Styled.DiscoverButton>
          </Styled.InteractionContainer>
          <Styled.ImageContainer>
            <Styled.ImageBackground>
              <Styled.BackgroundImageWide src={backgroundWide} />
              <Styled.ImageForeground>
                <Styled.NetworkImageWide
                  src={isLocaleDE ? networkImageWideDE : networkImageWideEN}
                />
              </Styled.ImageForeground>
            </Styled.ImageBackground>
          </Styled.ImageContainer>
        </Styled.GridItemRow>
      </Styled.GridContainerMargin>
    </Styled.CustomSection>
  ) : (
    <Styled.CustomSection data-qa="discover-network-section">
      <GridContainer variant="FullSize" data-qa="discover-network-container">
        <Styled.GridItemBackground data-qa="discover-network-row">
          <LightMode>
            <Styled.InteractionContainer>
              <Styled.HeadlineSmall size="xxlarge">
                <FormattedMessage id="NETWORK_LOGGED_OUT_DISCOVER_HEADLINE" />
              </Styled.HeadlineSmall>
              <Styled.DiscoverButton
                variant="secondary"
                size="large"
                to={buttonUrl}
                onClick={onClickCta}
              >
                <FormattedMessage id="NETWORK_LOGGED_OUT_DISCOVER_CTA" />
              </Styled.DiscoverButton>
            </Styled.InteractionContainer>
          </LightMode>
          <Styled.NetworkImageSmall
            $isLocaleDE={isLocaleDE}
            data-qa="image-container"
          />
        </Styled.GridItemBackground>
      </GridContainer>
    </Styled.CustomSection>
  );
};
